import React, { useMemo, useState } from 'react';
import { DatabaseSoundPackModel, DatabaseSoundPack } from '../../services/DatabaseSoundPack';
import { useSoundPacks } from '../../context/SoundPacksContext';

const sortByCreatedAtDesc = (a: DatabaseSoundPack, b: DatabaseSoundPack) => {
    if (!a.createdAt || !b.createdAt) return 0;
    return b.createdAt.localeCompare(a.createdAt);
};

function MakePublic() {
    const { soundPacks: allPacks, isLoading: loading } = useSoundPacks();
    const [publishing, setPublishing] = useState<string | null>(null);
    const [unpublishing, setUnpublishing] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [togglingFactory, setTogglingFactory] = useState<string | null>(null);

    // Derived slices — subscription auto-updates after publish/unpublish/delete.
    const soundPacks = useMemo(
        () => allPacks.filter(p => !p.public).sort(sortByCreatedAtDesc),
        [allPacks]
    );
    const publicPacks = useMemo(
        () => allPacks.filter(p => p.public).sort(sortByCreatedAtDesc),
        [allPacks]
    );

    const handlePublish = async (pack: DatabaseSoundPack) => {
        if (!pack.priceId) {
            alert('Please set a Stripe Price ID before publishing');
            return;
        }

        const confirmPublish = window.confirm(
            `Publish "${pack.name}" to public?\n\n` +
            `This will make the sound pack visible to all users.\n\n` +
            `Are you sure?`
        );

        if (!confirmPublish) return;

        setPublishing(pack.id!);

        try {
            await DatabaseSoundPackModel.setPublicStatus(pack.id!, true);
            alert(`"${pack.name}" published successfully!`);
        } catch (error) {
            console.error('Error publishing:', error);
            alert('Publication failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setPublishing(null);
        }
    };

    const handleUnpublish = async (pack: DatabaseSoundPack) => {
        const confirmUnpublish = window.confirm(
            `Unpublish "${pack.name}"?\n\n` +
            `This will make the sound pack invisible to regular users (preview mode).\n\n` +
            `Are you sure?`
        );

        if (!confirmUnpublish) return;

        setUnpublishing(pack.id!);

        try {
            await DatabaseSoundPackModel.setPublicStatus(pack.id!, false);
            alert(`"${pack.name}" unpublished successfully!`);
        } catch (error) {
            console.error('Error unpublishing:', error);
            alert('Unpublish failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setUnpublishing(null);
        }
    };

    const handleToggleFactory = async (pack: DatabaseSoundPack) => {
        const next = !pack.factoryDefault;

        const confirmToggle = window.confirm(
            next
                ? `Bundle "${pack.name}" into the plugin installer?\n\n` +
                  `It will be downloaded and shipped inside the next installer build ` +
                  `(when factory-pack bundling is enabled).\n\nAre you sure?`
                : `Stop bundling "${pack.name}" into the plugin installer?\n\n` +
                  `Future installer builds will no longer include it.\n\nAre you sure?`
        );

        if (!confirmToggle) return;

        setTogglingFactory(pack.id!);

        try {
            await DatabaseSoundPackModel.setFactoryDefault(pack.id!, next);
        } catch (error) {
            console.error('Error toggling factory default:', error);
            alert('Failed to update: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setTogglingFactory(null);
        }
    };

    const renderFactoryBadge = (pack: DatabaseSoundPack) => pack.factoryDefault && (
        <span className="ml-2 align-middle text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
            Factory
        </span>
    );

    const renderFactoryButton = (pack: DatabaseSoundPack) => (
        <button
            onClick={() => handleToggleFactory(pack)}
            disabled={togglingFactory === pack.id}
            title="Bundle this pack into the plugin installer"
            className={`px-4 py-2 rounded-md text-sm font-medium ${togglingFactory === pack.id
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : pack.factoryDefault
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
                }`}
        >
            {togglingFactory === pack.id
                ? 'Saving...'
                : pack.factoryDefault ? 'Factory ✓' : 'Make Factory'}
        </button>
    );

    const handleDelete = async (pack: DatabaseSoundPack) => {
        const confirmDelete = window.confirm(
            `DELETE "${pack.name}"?\n\n` +
            `This will permanently delete:\n` +
            `- The Firestore document\n` +
            `- All storage files (demo.zip, video, cover, etc.)\n\n` +
            `This action CANNOT be undone!\n\n` +
            `Type the pack name to confirm deletion.`
        );

        if (!confirmDelete) return;

        const typedName = window.prompt(`Type "${pack.name}" to confirm deletion:`);

        if (typedName !== pack.name) {
            alert('Pack name does not match. Deletion cancelled.');
            return;
        }

        setDeleting(pack.id!);

        try {
            await DatabaseSoundPackModel.deleteSoundPack(pack.id!);
            alert(`"${pack.name}" deleted successfully!`);
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Deletion failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setDeleting(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (soundPacks.length === 0 && publicPacks.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No sound packs found.</p>
                <p className="text-sm text-gray-400">Upload sound packs to get started.</p>
            </div>
        );
    }

    const readyForPublish = soundPacks.filter(pack => pack.priceId);
    const notReady = soundPacks.filter(pack => !pack.priceId);

    return (
        <div className="space-y-6">

            {/* Ready for publish */}
            {readyForPublish.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-green-700 mb-3">Ready to Publish ({readyForPublish.length})</h3>
                    <div className="space-y-3">
                        {readyForPublish.map((pack) => (
                            <div key={pack.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900">
                                            {pack.name}
                                            {renderFactoryBadge(pack)}
                                        </h4>
                                        <p className="text-xs text-gray-600 mt-1">
                                            Price ID: <code className="bg-white px-2 py-1 rounded">{pack.priceId}</code>
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            ID: <code className="bg-white px-2 py-1 rounded text-xs">{pack.id}</code>
                                        </p>
                                    </div>
                                    <div className="ml-4 flex gap-2">
                                        {renderFactoryButton(pack)}
                                        <button
                                            onClick={() => handlePublish(pack)}
                                            disabled={publishing === pack.id || deleting === pack.id}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${publishing === pack.id
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-green-600 text-white hover:bg-green-700'
                                                }`}
                                        >
                                            {publishing === pack.id ? 'Publishing...' : 'Publish'}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(pack)}
                                            disabled={deleting === pack.id || publishing === pack.id}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${deleting === pack.id
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-red-600 text-white hover:bg-red-700'
                                                }`}
                                        >
                                            {deleting === pack.id ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Public packs */}
            {publicPacks.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-blue-700 mb-3">Public Sound Packs ({publicPacks.length})</h3>
                    <div className="space-y-3">
                        {publicPacks.map((pack) => (
                            <div key={pack.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900">
                                            {pack.name}
                                            {renderFactoryBadge(pack)}
                                        </h4>
                                        <p className="text-xs text-gray-600 mt-1">
                                            Price ID: <code className="bg-white px-2 py-1 rounded">{pack.priceId || 'N/A'}</code>
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            ID: <code className="bg-white px-2 py-1 rounded text-xs">{pack.id}</code>
                                        </p>
                                    </div>
                                    <div className="ml-4 flex gap-2">
                                        {renderFactoryButton(pack)}
                                        <button
                                            onClick={() => handleUnpublish(pack)}
                                            disabled={unpublishing === pack.id}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${unpublishing === pack.id
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-orange-600 text-white hover:bg-orange-700'
                                                }`}
                                        >
                                            {unpublishing === pack.id ? 'Unpublishing...' : 'Make Private'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Not ready */}
            {notReady.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-yellow-700 mb-3">Missing Stripe Price ID ({notReady.length})</h3>
                    <div className="space-y-3">
                        {notReady.map((pack) => (
                            <div key={pack.id} className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900">
                                            {pack.name}
                                            {renderFactoryBadge(pack)}
                                        </h4>
                                        <p className="text-xs text-red-600 mt-1">No Stripe Price ID set</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            ID: <code className="bg-white px-2 py-1 rounded text-xs">{pack.id}</code>
                                        </p>
                                    </div>
                                    <div className="ml-4 flex gap-2">
                                        {renderFactoryButton(pack)}
                                        <span className="px-4 py-2 bg-yellow-200 text-yellow-800 rounded-md text-sm">
                                            Cannot Publish
                                        </span>
                                        <button
                                            onClick={() => handleDelete(pack)}
                                            disabled={deleting === pack.id}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${deleting === pack.id
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-red-600 text-white hover:bg-red-700'
                                                }`}
                                        >
                                            {deleting === pack.id ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                        Go to the "Link Stripe Price" tab to add price IDs to these packs.
                    </p>
                </div>
            )}
        </div>
    );
}

export default MakePublic;
